import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import { getBarGraphData } from "../../app/api/overallAnalysis";
import { TrendsFilterType } from "../../constants/constants";
import { BarData } from "../../interface/Trends";
import { User } from "../../types";
import {
  getTrendFiltersForGraphs,
  getTrendFiltersFromSession,
} from "./graphHelperUtils";
import {
  formatOverallAnalysisCellDisplay,
  getOverallAnalysisDynamicColumnKeys,
  getOverallAnalysisFixedColumnValue,
  getOverallAnalysisTableAttributeColumns,
  OVERALL_ANALYSIS_LEADING_FIXED_TABLE_COLUMNS,
  OVERALL_ANALYSIS_TRAILING_FIXED_TABLE_COLUMNS,
} from "./overallAnalysisColumns";
import { getKeyFromValueForTrendQueryType } from "../commonUtils";
import store from "../../app/store";
import { setOverallAnalysis } from "../../app/slices/trendSlice";
import { filterArchivedGraphRows } from "../archivedTrends";
import { toPng } from "html-to-image";
import {
  drawPdfHeader,
  drawPdfSectionTitle,
  drawPdfTitle,
  elegantTablePageDecorator,
  loadPoppinsFonts,
  PDF_OVERALL_ANALYSIS_TABLE_STYLES,
} from "./pdfEleganceUtils";
import { svgToBase64DataUrl } from "./svgDownloadUtils";

export const generateOverallAnalysisS3JSON = async (user: User | null) => {
  try {
    const trendFilters = getTrendFiltersForGraphs();
    if (!trendFilters) return null;
    const filterType = getKeyFromValueForTrendQueryType(
      trendFilters.query_type!
    );
    const overallAnalysisJSON = await formatAndSetDataForOverallAnalysis(
      filterType,
      user,
      undefined,
      undefined,
      "Trend Score"
    );
    if (!(Array.isArray(overallAnalysisJSON) && overallAnalysisJSON.length)) {
      console.error("No Overall Analysis Data");
      return null;
    }
    return {
      filterType,
      overallAnalysisJSON,
    };
  } catch (error) {
    console.error("Error while generating image", error);
    toast.error("Error while generating image", {
      hideProgressBar: true,
    });
    return null;
  }
};

export const formatAndSetDataForOverallAnalysis = async (
  filterType: TrendsFilterType,
  user: User | null,
  setBarData?: React.Dispatch<React.SetStateAction<BarData[] | null>>,
  setDynamicColumns?: React.Dispatch<React.SetStateAction<string[]>>,
  sortingType?: string,
  archivedTrendNames?: string[]
) => {
  const trendFilters = getTrendFiltersFromSession();
  if (!trendFilters) return null;

  const response = await getBarGraphData(trendFilters, user);
  if (!response) return;

  const names = archivedTrendNames ?? store.getState().trends.archivedTrends;
  const filteredResponse = filterArchivedGraphRows<BarData>(response, names);

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  store.dispatch(setOverallAnalysis(filteredResponse as any));

  if (sortingType === "Trend Score") {
    setBarData && setBarData(filteredResponse);
  } else {
    const sortedByImpact = [...filteredResponse].sort(
      (a: BarData, b: BarData) =>
        Number(b["Impact Score"]) - Number(a["Impact Score"])
    );

    setBarData && setBarData(sortedByImpact);
  }

  //Fetch the table contents
  if (setDynamicColumns && filteredResponse?.length > 0) {
    setDynamicColumns(
      getOverallAnalysisDynamicColumnKeys(
        filteredResponse[0] as Record<string, unknown>
      )
    );
  }
  return filteredResponse;
};

export const handleDownload = async (
  setDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>,
  barData: BarData[] | null,
  dynamicColumns: string[],
  selectedMaterials?: string[],
  svgContainer?: HTMLElement
) => {
  setDownloadLoading(true);
  try {
    const attributeColumns = getOverallAnalysisTableAttributeColumns(
      dynamicColumns,
      selectedMaterials
    );

    const captureSvgContainer = async (): Promise<{
      imgData: string;
      width: number;
      height: number;
    } | null> => {
      if (!svgContainer) return null;
      try {
        const imgData = await toPng(svgContainer, {
          pixelRatio: 4,
          cacheBust: true,
          backgroundColor: "#ffffff",
        });
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () =>
            reject(new Error("Failed to load captured image"));
          img.src = imgData;
        });
        return {
          imgData,
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
      } catch {
        return null;
      }
    };

    const captureSvgElement = (): Promise<{
      imgData: string;
      width: number;
      height: number;
    } | null> =>
      new Promise(resolve => {
        const svgElement = document.querySelector(".recharts-wrapper svg");
        if (!svgElement) {
          resolve(null);
          return;
        }
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
        const style = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "style"
        );
        style.textContent = `
      * { font-family: 'Poppins', sans-serif; }
    `;
        clonedSvg.insertBefore(style, clonedSvg.firstChild);

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const width = svgElement.getBoundingClientRect().width;
        const height = svgElement.getBoundingClientRect().height;
        canvas.width = width * 4;
        canvas.height = height * 4;
        context?.scale(4, 4);

        const img = new Image();
        img.onload = () => {
          context?.drawImage(img, 0, 0);
          resolve({
            imgData: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          });
        };
        img.onerror = () => resolve(null);
        img.src = svgToBase64DataUrl(
          new XMLSerializer().serializeToString(clonedSvg)
        );
      });

    const captured = svgContainer
      ? await captureSvgContainer()
      : await captureSvgElement();

    if (!captured) {
      toast.error("No graph available to download");
      setDownloadLoading(false);
      return;
    }

    const { imgData, width: imgWidth, height: capturedImgHeight } = captured;

    // Create PDF in A3 size
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3",
    });

    await loadPoppinsFonts(pdf);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calculate table width first
    const tableWidth = pageWidth * 0.8;
    const tableMargin = (pageWidth - tableWidth) / 2;

    // Use same width for graph
    const chartWidth = tableWidth;
    const imgHeight = (capturedImgHeight / imgWidth) * chartWidth;
    const xOffset = tableMargin;

    drawPdfHeader(pdf, pageWidth);
    drawPdfTitle(pdf, "Overall Analysis Graph", pageWidth, 70, false);

    pdf.addImage(imgData, "PNG", xOffset, 100, chartWidth, imgHeight);

    const tableTitleY = 100 + imgHeight + 40;

    drawPdfSectionTitle(pdf, "Overall Analysis Table", pageWidth, tableTitleY);

    const formatFixedCells = (
      row: BarData,
      columns: typeof OVERALL_ANALYSIS_LEADING_FIXED_TABLE_COLUMNS
    ) =>
      columns.map(({ formatKey }) =>
        formatOverallAnalysisCellDisplay(
          formatKey,
          getOverallAnalysisFixedColumnValue(
            row as Record<string, unknown>,
            formatKey
          )
        )
      );

    const tableData = barData?.map(row => [
      ...formatFixedCells(row, OVERALL_ANALYSIS_LEADING_FIXED_TABLE_COLUMNS),
      ...attributeColumns.map(column =>
        formatOverallAnalysisCellDisplay(
          column,
          (row as Record<string, unknown>)[column]
        )
      ),
      ...formatFixedCells(row, OVERALL_ANALYSIS_TRAILING_FIXED_TABLE_COLUMNS),
    ]);

    const tableHead = [
      [
        ...OVERALL_ANALYSIS_LEADING_FIXED_TABLE_COLUMNS.map(c => c.headerLabel),
        ...attributeColumns,
        ...OVERALL_ANALYSIS_TRAILING_FIXED_TABLE_COLUMNS.map(
          c => c.headerLabel
        ),
      ],
    ];

    // @ts-expect-error: autoTable is not recognized by TypeScript
    pdf.autoTable({
      head: tableHead,
      body: tableData || [],
      startY: tableTitleY + 30,
      ...PDF_OVERALL_ANALYSIS_TABLE_STYLES,
      tableWidth: tableWidth,
      margin: {
        left: tableMargin,
        right: tableMargin,
        bottom: 60,
      },
      didDrawPage: elegantTablePageDecorator(pageWidth, pageHeight),
    });

    pdf.save("Overall Analysis.pdf");
  } catch (error) {
    console.error("Error generating Overall Analysis report", error);
    toast.error("Error generating report");
  } finally {
    setDownloadLoading(false);
  }
};
